import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import { DrawerContent } from "./DrawerContent";
import { Logo } from "../components/Logo";
import { useConfig } from "../theme/ConfigContext";
import { useAuth } from "../auth/AuthContext";
import { isLikelyProductionBackend } from "../lib/environmentBanner";
import { ADMIN_NAV_ITEMS } from "./navItems";
import { ProviderSwitcher } from "../components/ProviderSwitcher";
import { useProvider } from "../providers/ProviderContext";

const DRAWER_WIDTH = 260;
const MINI_DRAWER_WIDTH = 64;

/**
 * The Mantis Free `layout/Dashboard` shell (Drawer + Header + `<Outlet/>`) — reconstructed, not
 * line-for-line vendored (docs/337 §"Vendor only the scaffold"): the real Mantis `Dashboard/index.
 * jsx` composes its own `Drawer/`, `Header/`, and `Footer/` sub-packages (search box, notification
 * bell, profile menu, breadcrumbs, a `MiniDrawerStyled` wrapper) wired to demo routes/mock auth
 * this evaluation build explicitly skips (docs/337's own "skip entirely" list). What's kept is the
 * STRUCTURE — a collapsible left Drawer + a fixed AppBar + a routed `<Outlet/>` body — rebuilt
 * directly against this app's own `ADMIN_NAV_ITEMS`/`AuthContext`, mirroring
 * `apps/admin-web/src/layout/DefaultLayout.tsx`'s role (same live-production-backend banner) with
 * MUI components instead of CoreUI ones.
 */
export function Dashboard() {
  const { drawerOpen, toggleDrawer, mode: themeMode, toggleMode } = useConfig();
  const { user, mode, signOut, client, token, backendUrl } = useAuth();
  const { deployment, capabilities, capabilitiesLoading, capabilitiesWarning } = useProvider();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isPhoneWidth = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const width = drawerOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH;
  const active = ADMIN_NAV_ITEMS.find(
    (item) => location.pathname === item.route || location.pathname.startsWith(`${item.route}/`),
  );

  // docs/357-ADMIN-ANALYTICS-AND-ACCESS-TRACKING.md v0 §3.2 — a page-access
  // beacon fired on every admin route CHANGE (this effect's own dep is
  // `location.pathname`, not every render). Only fires for a route that
  // matches a real, trackable `ADMIN_NAV_ITEMS` entry (`active`) — the same
  // allow-list the Worker independently re-validates server-side. A descriptor
  // can opt out when its frontend-only route has no Worker allow-list entry.
  // Fire-and-forget: a failed/blocked (tracking-off) call never surfaces to the
  // user and never blocks navigation.
  useEffect(() => {
    if (!client || !token || !active || active.track === false) return;
    void client.adminTrack(token, { event: "page_view", path: active.route }).catch(() => {
      // Swallowed on purpose — a page-view beacon is best-effort telemetry,
      // never something worth surfacing as a UI error.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, client, token]);

  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${width}px)` },
          ml: { md: `${width}px` },
          borderBottom: "1px solid",
          borderColor: "divider",
          transition: (theme) => theme.transitions.create(["width", "margin"]),
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <IconButton
            edge="start"
            onClick={() => (isPhoneWidth ? setMobileDrawerOpen(true) : toggleDrawer())}
            aria-label="Toggle navigation"
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h5" sx={{ flexGrow: 1 }} noWrap>
            {active?.label ?? "Equipped Admin"}
          </Typography>
          {deployment.showSwitcher ? (
            <Box sx={{ display: { xs: "none", md: "block" } }}><ProviderSwitcher /></Box>
          ) : null}
          {capabilitiesLoading ? (
            <Chip label="Checking provider…" size="small" variant="outlined" />
          ) : capabilities?.legacy ? (
            <Chip label="Limited — older provider" size="small" color="warning" />
          ) : capabilities?.providerTooNew ? (
            <Chip label="Newer provider" size="small" color="warning" />
          ) : null}
          <Typography variant="body2" color="text.secondary" noWrap sx={{ display: { xs: "none", sm: "block" } }}>
            {mode === "key" ? "Admin key" : user?.email}
          </Typography>
          <Tooltip title={`Switch to ${themeMode === "dark" ? "light" : "dark"} mode`}>
            <IconButton
              onClick={toggleMode}
              aria-label={`Switch to ${themeMode === "dark" ? "light" : "dark"} mode`}
              color="inherit"
            >
              {themeMode === "dark" ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={() => {
              signOut();
              navigate("/login", { replace: true });
            }}
          >
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          [`& .MuiDrawer-paper`]: { width: DRAWER_WIDTH, boxSizing: "border-box" },
        }}
      >
        <Toolbar sx={{ px: 2 }}><Logo /></Toolbar>
        <DrawerContent />
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          width,
          flexShrink: 0,
          display: { xs: "none", md: "block" },
          [`& .MuiDrawer-paper`]: {
            width,
            boxSizing: "border-box",
            borderRight: "1px solid",
            borderColor: "divider",
            overflowX: "hidden",
            transition: (theme) => theme.transitions.create("width"),
          },
        }}
      >
        <Toolbar sx={{ px: 2 }}>{drawerOpen ? <Logo /> : null}</Toolbar>
        <DrawerContent />
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${width}px)` }, minWidth: 0 }}>
        <Toolbar />
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {isLikelyProductionBackend(backendUrl) ? (
            <Alert severity="warning" sx={{ mb: 3 }}>
              ⚠️ Connected to the <strong>live production</strong> backend — every action here (set
              password, permanent delete, add user, …) affects real accounts, whether this build is
              "preview" or "production."
            </Alert>
          ) : null}
          {capabilitiesWarning ? (
            <Alert severity="warning" sx={{ mb: 3 }}>{capabilitiesWarning}</Alert>
          ) : null}
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
